import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';
import { SanitizedContent } from '@/components/ui/sanitized-content';

interface RevisionDiffViewerProps {
  oldContent: string;
  newContent: string;
  oldTitle: string;
  newTitle: string;
}

export const RevisionDiffViewer = ({
  oldContent,
  newContent,
  oldTitle,
  newTitle
}: RevisionDiffViewerProps) => {
  const changes = useMemo(() => {
    const titleChanged = oldTitle !== newTitle;
    const contentChanged = oldContent !== newContent;
    
    // Simple word-based diff
    const oldWords = oldContent.split(/\s+/);
    const newWords = newContent.split(/\s+/);
    
    const additions = newWords.length - oldWords.length;
    const contentLength = newContent.length - oldContent.length;
    
    return {
      titleChanged,
      contentChanged,
      additions: additions > 0 ? additions : 0,
      deletions: additions < 0 ? Math.abs(additions) : 0,
      characterDiff: contentLength,
    };
  }, [oldContent, newContent, oldTitle, newTitle]);

  return (
    <Card className="border-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />
          Version Comparison
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {changes.titleChanged && (
          <div>
            <p className="text-sm font-medium mb-2">Title Change:</p>
            <div className="space-y-1">
              <div className="p-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                <span className="text-xs text-red-700 dark:text-red-300">- Old: </span>
                <span className="text-sm line-through">{oldTitle}</span>
              </div>
              <div className="p-2 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded">
                <span className="text-xs text-green-700 dark:text-green-300">+ New: </span>
                <span className="text-sm font-medium">{newTitle}</span>
              </div>
            </div>
          </div>
        )}

        {changes.contentChanged && (
          <div>
            <p className="text-sm font-medium mb-2">Content Changes:</p>
            <div className="flex gap-3">
              {changes.additions > 0 && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  +{changes.additions} words added
                </Badge>
              )}
              {changes.deletions > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  -{changes.deletions} words removed
                </Badge>
              )}
              <Badge variant="outline">
                {changes.characterDiff > 0 ? '+' : ''}{changes.characterDiff} characters
              </Badge>
            </div>
          </div>
        )}

        {!changes.titleChanged && !changes.contentChanged && (
          <p className="text-sm text-muted-foreground">No changes detected</p>
        )}

        {changes.contentChanged && (
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground mb-2">Content Preview:</p>
            <div className="space-y-2">
              <div className="p-3 bg-muted rounded-lg max-h-32 overflow-y-auto">
                <p className="text-xs font-medium mb-1 text-muted-foreground">Previous Version:</p>
                <SanitizedContent 
                  content={oldContent.substring(0, 200) + '...'}
                  className="text-sm prose dark:prose-invert max-w-none line-clamp-3"
                />
              </div>
              <div className="p-3 bg-primary/5 rounded-lg max-h-32 overflow-y-auto">
                <p className="text-xs font-medium mb-1 text-primary">Current Version:</p>
                <SanitizedContent 
                  content={newContent.substring(0, 200) + '...'}
                  className="text-sm prose dark:prose-invert max-w-none line-clamp-3"
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
